import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, EventEmitter, Input, Output, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { NzPopoverModule } from 'ng-zorro-antd/popover';
import { NzProgressModule } from 'ng-zorro-antd/progress';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzSegmentedModule } from 'ng-zorro-antd/segmented';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzStepsModule } from 'ng-zorro-antd/steps';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { take } from 'rxjs';
import { PhoneCallButtonComponent } from '../../../basic/buttons/phone-call-button/phone-call-button.component';
import { QuestionButtonComponent } from '../../../basic/buttons/question-button/question-button.component';
import { GoalService } from '../../../core/goal/goal.service';
import { MethodologyService } from '../../../core/methodology/methodology.service';
import { ProductService } from '../../../core/product/product.service';
import { getColdCallingDifficultProspectPrompt } from '../../../core/prompt/scenario/cold-calling-difficult-prospect.prompt';
import { Resume } from '../../../core/resume/resume';
import { ResumeService } from '../../../core/resume/resume.service';
import { SessionTemplate, SessionTemplateUnSafeSubData } from '../../../core/session-template/session-template';
import { SessionTemplateService } from '../../../core/session-template/session.template.service';
import { SessionCreativity, SessionPerformance } from '../../../core/session/session';
import { SessionService } from '../../../core/session/session.service';
import { SalesCycle } from '../../../core/workspace/workspace';
import { WorkspaceHolderService } from '../../../core/workspace/workspace.holder.service';
import { surveyAnimations } from '../../../shared/animation/survey.animation';

import { NzInputNumberModule } from 'ng-zorro-antd/input-number';
import { Objection, ObjectionType } from '../../../core/objection/objection';
import { ObjectionService } from '../../../core/objection/objection.service';
import { ProfileCardSmallComponent } from '../../profile-card/profile-card-small/profile-card-small.component';
import { ProfileCardComponent } from '../../profile-card/profile-card.component';
import { EditResumeComponent } from '../../resume/edit-resume/edit-resume.component';

@Component({
  selector: 'app-new-survey-session-template',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,

    NzIconModule,
    NzInputModule,
    NzButtonModule,
    NzTableModule,
    NzPopoverModule,
    NzModalModule,
    NzAlertModule,
    NzFormModule,
    NzRadioModule,
    NzSelectModule,
    NzSegmentedModule,
    NzPopconfirmModule,
    NzDividerModule,
    NzProgressModule,
    NzCheckboxModule,
    NzToolTipModule,
    MatTooltipModule,
    NzStepsModule,
    NzInputNumberModule,

    QuestionButtonComponent,
    ProfileCardSmallComponent,
    ProfileCardComponent,
    EditResumeComponent,
    PhoneCallButtonComponent,
  ],
  templateUrl: './new-survey-session-template.component.html',
  styleUrl: './new-survey-session-template.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: surveyAnimations,
})
export class NewSurveySessionTemplateComponent {
  @Input() isVisible = false;
  @Output() isVisibleChange = new EventEmitter<boolean>();

  isLoading = signal<boolean>(false);

  isEditResumeVisible = false;

  enableAddProduct = false;
  enableAddGoal = false;

  resumeList = toSignal(this.resumeService.resumeList$(), { initialValue: [] });
  productList = toSignal(this.productService.productList$(), { initialValue: [] });
  goalList = toSignal(this.goalService.goalList$(), { initialValue: [] });
  methodologyList = toSignal(this.methodologyService.methodologyList$(), { initialValue: [] });

  emotionalSelectList: { item: Objection; isSelected: boolean }[] = [];
  factSelectList: { item: Objection; isSelected: boolean }[] = [];

  readonly totalSteps = 10;
  currentProgress = computed(() => {
    return Math.round((this.currentStepIndex() / this.totalSteps) * 100);
  });

  howFamiliarPersonaOptions = ['No', 'Yes'];
  howFamiliarPersonaIndex = 0;

  isAutoGenerateScenarioName = false;
  isAutoGenerateProductName = true;

  scenarioName = '';
  targetCallDuration = 30;
  resumeId = '';
  productId = '';
  goalId = '';
  salesCycleId = '';
  methodologyId = '';

  newProductName = '';
  newProductDescription = '';

  newGoalName = '';
  newGoalDescription = '';

  newInstruction = '';

  currentStepIndex = signal<number>(0);

  createdSessionTemplate?: SessionTemplate;

  constructor(
    private workspaceService: WorkspaceHolderService,
    private messageService: NzMessageService,
    private sessionTemplateService: SessionTemplateService,
    private methodologyService: MethodologyService,
    private goalService: GoalService,
    private productService: ProductService,
    private resumeService: ResumeService,
    private sessionService: SessionService,
    private objectionService: ObjectionService,
    private router: Router
  ) {}

  ngOnInit() {
    this.salesCycleId =
      this.workspaceService.currentWorkspace().settings!.salesCycleList[this.currentSalesCycleIndex].id;

    this.objectionService
      .itemList$()
      .pipe(take(1))
      .subscribe((list) => {
        list.forEach((item) => {
          if (item.type === ObjectionType.emotional) {
            this.emotionalSelectList.push({ item, isSelected: false });
          } else {
            this.factSelectList.push({ item, isSelected: false });
          }
        });
      });
  }

  createdResume?: Resume;

  instructionPlaceholder = `Please be as descriptive as possible on what you want the AI customer to do. For example: “Ask the seller these 5 specific objections” or “Tell the seller [XYZ] reasons why you’re an unhappy customer” `;

  currentSalesCycleIndex = 0;
  onCurrentSalesCycleChange(index: number) {
    this.salesCycleId = this.workspaceService.currentWorkspace().settings!.salesCycleList[index].id;
  }

  salesCycleTitleList = computed(() => {
    return this.workspaceService.currentWorkspace().settings?.salesCycleList.map((c) => c.title) ?? [];
  });

  onBookMeetingClick() {
    window.open('https://calendly.com/peakmojo-ai/book-a-demo', '_blank');
  }

  onSalesCycleChange(saleCycle: SalesCycle) {
    this.salesCycleId = saleCycle.id;
  }

  onFillOutInstructionClick() {
    this.newInstruction = getColdCallingDifficultProspectPrompt();
  }

  onCreateCustomerClick() {
    this.isLoading.set(true);

    const productDescription = this.productList()?.find((c) => c.id === this.productId)?.description;

    if (!productDescription) {
      this.messageService.error('Cannot create the customer ');
    }

    this.resumeService
      .createResumeByProduct$(productDescription)
      .pipe(take(1))
      .subscribe((resume) => {
        this.createdResume = resume;
        this.resumeId = resume.id;

        this.isLoading.set(false);
      });
  }

  onEnableAddProduct() {
    this.enableAddProduct = !this.enableAddProduct;
  }

  onAddNewProduct() {
    this.isLoading.set(true);

    if (this.isAutoGenerateProductName) {
      this.newProductName = '';
    }

    this.productService
      .createProductByDescription$(this.newProductDescription, { productName: this.newProductName })
      .pipe(take(1))
      .subscribe((data) => {
        this.newProductName = data.title ?? '';
        this.productId = data.id;
        this.isLoading.set(false);
        this.enableAddProduct = false;
      });
  }

  onEnableAddGoal() {
    this.enableAddGoal = !this.enableAddGoal;
  }

  onAddNewGoal() {
    this.isLoading.set(true);

    this.goalService
      .addGoal$(this.newGoalName, this.newGoalDescription, {})
      .pipe(take(1))
      .subscribe((data) => {
        this.newGoalName = data.title ?? '';
        this.goalId = data.id;
        this.isLoading.set(false);
        this.enableAddGoal = false;
      });
  }

  getResumeText(resume?: Resume) {
    if (!resume) {
      return '';
    }
    return `${resume.subData.content?.firstName} ${resume.subData.content?.lastName} - ${resume.subData.content?.mostRecentPosition} @
    ${resume.subData.content?.mostRecentCompany}`;
  }

  onViewEditClick() {
    this.isEditResumeVisible = true;
  }

  onPreviousClick() {
    this.currentStepIndex.set(this.currentStepIndex() - 1);
  }

  onNextClick() {
    if (this.currentStepIndex() === 2 && !this.productId) {
      this.messageService.error('Please select or create a product.', { nzDuration: 10000 });
      return;
    }

    if (this.currentStepIndex() === 3 && !this.resumeId) {
      this.messageService.error('Please select or create a customer.', { nzDuration: 10000 });
      return;
    }

    this.currentStepIndex.set(this.currentStepIndex() + 1);
  }

  onCancelClick() {
    this.isVisible = false;
    this.isVisibleChange.emit(this.isVisible);
  }

  onIsVisibleChange(isVisible: boolean) {
    this.isVisibleChange.emit(isVisible);
  }

  getGoal() {
    return this.goalList().find((c) => c.id === this.goalId);
  }

  getProduct() {
    return this.productList().find((c) => c.id === this.productId);
  }

  getResume() {
    return this.resumeList().find((c) => c.id === this.resumeId);
  }

  onFinishClick() {
    this.isLoading.set(true);

    if (this.currentStepIndex() === 5 && !this.goalId) {
      this.messageService.error('Please select or create a goal.', { nzDuration: 10000 });
      return;
    }

    const emotionalObjectionIdList: string[] = [];
    const factObjectionIdList: string[] = [];

    this.emotionalSelectList.forEach((c) => {
      if (c.isSelected) {
        emotionalObjectionIdList.push(c.item.id);
      }
    });

    this.factSelectList.forEach((c) => {
      if (c.isSelected) {
        factObjectionIdList.push(c.item.id);
      }
    });

    const sessionTemplateUnsafeSubData: SessionTemplateUnSafeSubData = {
      language: 'en',

      instruction: this.newInstruction,

      goalId: this.goalId,
      methodologyId: this.methodologyId,
      resumeId: this.resumeId,
      productId: this.productId,
      salesCycleId: this.salesCycleId,

      isFamiliarProduct: this.howFamiliarPersonaIndex === 0 ? false : true,

      performance: SessionPerformance.Quality, // Hardcode Quality
      creativity: SessionCreativity.Creative,

      emotionalObjectionIdList: emotionalObjectionIdList,
      factObjectionIdList: factObjectionIdList,
    };

    if (this.scenarioName) {
      this.sessionTemplateService
        .add$(this.scenarioName, sessionTemplateUnsafeSubData)
        .pipe(take(1))
        .subscribe((sessionTemplate) => {
          this.createdSessionTemplate = sessionTemplate;
          this.currentStepIndex.set(this.currentStepIndex() + 1);

          this.isLoading.set(false);
        });
    } else {
      this.sessionTemplateService
        .addWithoutTitle$(this.getResume()!, this.getGoal()!, this.getProduct()!, sessionTemplateUnsafeSubData)
        .pipe(take(1))
        .subscribe((sessionTemplate) => {
          this.scenarioName = sessionTemplate.title ?? '';
          this.createdSessionTemplate = sessionTemplate;
          this.currentStepIndex.set(this.currentStepIndex() + 1);

          this.isLoading.set(false);
        });
    }
  }

  onStartCallClick() {
    if (!this.isLoading()) {
      if (this.createdSessionTemplate) {
        this.isLoading.set(true);
        this.sessionService
          .addSession$(this.createdSessionTemplate.id)
          .pipe(take(1))
          .subscribe((data) => {
            if (!data) {
              return;
            }
            this.sessionService
              .launchSession$(data)
              .pipe(take(1))
              .subscribe(() => {
                this.isLoading.set(false);
                this.router.navigate(['', 'practice', data.id]);
              });
          });
      }
    }
  }
}
